import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ManualCheck, PayTypes } from '../../../models/payroll';
import { CompaniesService } from '../../../services/companies.service';
import { PayrollService } from '../../../services/payroll.service';
import { SharedService } from '../../../services/shared.service';
import * as _ from 'underscore';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'lib-manual-checks',
  templateUrl: './manual-checks.component.html',
  styleUrls: ['./manual-checks.component.scss'],
  standalone: false
})
export class ManualChecksComponent implements OnInit {
  public calendarId: number | null;
  public clientCode: string;
  public manualChecks: ManualCheck[];
  public selectedManualChecks: ManualCheck[] = [];
  public loaded: boolean;

  private readonly toastService = inject(ToastService);

  constructor(
    private payrollService: PayrollService,
    private companyService: CompaniesService,
    private sharedService: SharedService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.updateCheckNumber = _.debounce(this.updateCheckNumber, 500);
  }

  ngOnInit(): void {
    const paramMap = this.route.snapshot.paramMap;
    const queryParams = this.route.snapshot.queryParamMap;
    this.calendarId = queryParams.has('calendar_id') ? +queryParams.get('calendar_id') : null;
    this.clientCode = paramMap.get('client_code');

    // fetch the payroll start data and check if an active payroll is present
    this.payrollService.getPayrollStartData(this.clientCode).subscribe(resp => {
      if (!resp.ActivePayroll) {
        this._getManualChecks();
        return;
      }

      switch (resp.ActivePayroll.PayType) {
        case PayTypes.Regular:
        case PayTypes.OffCycle:
          this.sharedService.manualChecks = [];
          this.sharedService.timeclockSelection = {};
          this._goToPayrollStart();
          break;
        case PayTypes.Manual:
          this._getManualChecks();
          break;
        default:
          console.error(
            `Unhandled payroll type ${resp.ActivePayroll.PayType}  Redirecting to payroll hub.`
          );
          this._goToPayrollHub();
          break;
      }
    });
  }

  //#region Public Methods

  public goToTimeclock() {
    // if user selects manual check(s), set the shared service public property
    // we set manual checks to an empty array so that we know we have visited this page
    // at the payroll start screen
    if (this.selectedManualChecks) {
      this.sharedService.manualChecks = this.selectedManualChecks;
    } else {
      this.sharedService.manualChecks = [];
    }

    // for off cycle payrolls, we skip the timeclock page and go straight to payroll
    const map = this.route.snapshot.queryParamMap;
    if (map.has('payType') && map.get('payType') == '3') {
      this._goToPayrollStart();
    } else {
      this.router.navigate([`/client/${this.clientCode}/timeclock-check`], {
        queryParams: this._getQueryParams()
      });
    }
  }

  public onManualCheckChange(event) {
    const index = this.selectedManualChecks.findIndex(mc => mc.Id == event.target.id);

    if (index > -1) {
      // remove item as user has unchecked
      this.selectedManualChecks.splice(index, 1);
    } else {
      this.selectedManualChecks.push(this.manualChecks.find(mc => mc.Id == event.target.id));
    }
  }

  public updateCheckNumber(manualCheckId) {
    const manualCheck = this.manualChecks.find(mc => mc.Id == manualCheckId);

    this.companyService
      .updateManualCheck(
        this.clientCode,
        manualCheck.PayrollYear,
        manualCheck.PayrollSequence,
        manualCheck.Id,
        manualCheck
      )
      .subscribe(() => {
        this.toastService.show('Save successful');
      });
  }

  //#endregion

  //#region Private Methods

  private _getManualChecks() {
    // FIXME: will need addressed when multiple locations are enabled
    this.companyService.getManualChecks(this.clientCode, '0').subscribe(resp => {
      // sanity check to make sure we're getting a proper object
      // and not one with value(s) of null
      if (resp.length && resp[0] != null) {
        this.manualChecks = resp;
        // task 16234 - select each manual check by default
        this.selectedManualChecks = [...this.manualChecks];
      } else {
        // no manual checks fetched, navigate user to timeclock check
        this.goToTimeclock();
      }

      // prevent page flicker
      this.loaded = true;
    });
  }

  private _getQueryParams(params?: {}) {
    // if calendar_id query param exists, append it back to the query params
    // to the payroll start page
    if (this.calendarId) {
      params = { ...params, ...{ calendar_id: this.calendarId } };
    }

    if (this.route.snapshot.queryParamMap.has('payType')) {
      params = { ...params, ...{ payType: this.route.snapshot.queryParamMap.get('payType') } };
    }

    return params;
  }

  private _goToPayrollStart(params?: {}) {
    this.router.navigate([`/client/${this.clientCode}/payroll`], {
      queryParams: this._getQueryParams()
    });
  }

  private _goToPayrollHub() {
    this.router.navigate([`/client/${this.clientCode}/payroll-landing`]);
  }

  //#endregion
}
